import { FC, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { Lock } from '@mui/icons-material'
import { Card, CardContent, CardHeader, Dialog, DialogContent, DialogProps, Divider, Grid, Link, Typography } from '@mui/material'
import { Box } from '@mui/system'

import { useGetTutorialVideosQuery } from '../../api/core'
import GRDialogTitle from '../../components/GRDialogTitle/GRDialogTitle'
import { ImageSlider } from '../../components/ImageSlider/ImageSlider'
import FtueVideoPlayer from '../../features/ftue-videos/components/FtueVideoPlayer/FtueVideoPlayer'
import GameIcon from '../../features/game/components/GameIcon/GameIcon'
import lockedFeatureService from '../../services/LockedFeatureService'
import { LockedFeature as ILockedFeature, LockedFeatureId, LockedGameSummary } from '../../types/LockedFeature'
import { ContactButton } from '../ContactButton/ContactButton'
import GRCircularProgress from '../GRCircularProgress/GRCircularProgress'
import './LockedFeature.scss'

/**
 * Components for displaying Locked feature card and dialog
 */

interface LockedFeatureProps {
  lockedFeatureId: LockedFeatureId
  hideImage?: boolean // hide image in card
  game?: LockedGameSummary // the locked game the dialog was opened for, shown above the copy
}

const LockedFeatureCard: FC<LockedFeatureProps> = ({ lockedFeatureId, hideImage = false }) => {
  const cardData = useMemo(() => {
    return lockedFeatureService.getLockedFeatureCardData(lockedFeatureId)
  }, [lockedFeatureId])

  return (
    <Card>
      <CardHeader title={<LockedFeatureTitle />} />
      <Divider />
      <CardContent>
        <LockedFeatureContent cardData={cardData} hideImage={hideImage} />
      </CardContent>
    </Card>
  )
}

type LockedFeatureDialogProps = DialogProps &
  LockedFeatureProps & {
    onClose?: () => void
  }

const LockedFeatureDialog: FC<LockedFeatureDialogProps> = ({ lockedFeatureId, onClose, game, ...dialogProps }) => {
  const { open } = dialogProps

  const cardData = useMemo(() => {
    return lockedFeatureService.getLockedFeatureCardData(lockedFeatureId)
  }, [lockedFeatureId])

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md">
      <GRDialogTitle onClose={onClose}>
        <LockedFeatureTitle titleTranslationKey={cardData.dialogTitleTranslationKey} />
      </GRDialogTitle>
      <DialogContent dividers>
        <LockedFeatureContent cardData={cardData} game={game} />
      </DialogContent>
    </Dialog>
  )
}

const LockedFeatureTitle: FC<{ titleTranslationKey?: string }> = ({ titleTranslationKey }) => {
  const { t } = useTranslation()

  return (
    <Box>
      <Lock style={{ top: '5px', position: 'relative', marginRight: '5px' }} /> {t(titleTranslationKey ?? 'feature:upgrade_your_plan_to_unlock_this_feature')}
    </Box>
  )
}

const LockedFeatureContent: FC<{ cardData: ILockedFeature; hideImage?: boolean; game?: LockedGameSummary }> = ({ cardData, hideImage, game }) => {
  const { t } = useTranslation()
  const { data: tutorialVideos, isLoading: isTutorialLoading } = useGetTutorialVideosQuery(undefined, { skip: !cardData.content.rightVideoId })
  const selectedVideo = useMemo(
    () => tutorialVideos?.find((video) => video.videoId === cardData.content.rightVideoId),
    [cardData.content.rightVideoId, tutorialVideos]
  )

  const gridMediumWidth = useMemo(() => {
    if (!!cardData.content.rightVideoId) {
      return 5
    }

    return hideImage || !cardData.content.rightUrl ? 12 : 5
  }, [cardData.content.rightUrl, cardData.content.rightVideoId, hideImage])

  const imageUrls = useMemo(() => {
    if (!cardData.content.rightUrl) return []
    return Array.isArray(cardData.content.rightUrl) ? cardData.content.rightUrl : [cardData.content.rightUrl]
  }, [cardData.content.rightUrl])

  return (
    <Grid container spacing={4} className="LockedFeatureContent">
      <Grid item xs={12} md={gridMediumWidth}>
        {game && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }} data-testid="locked-game-header">
            <Box aria-hidden="true" sx={{ display: 'flex', flexShrink: 0 }}>
              <GameIcon src={game.iconUrl} gameName={game.name} size="small" />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }} noWrap>
                {game.name}
              </Typography>
              {game.artist && (
                <Typography variant="caption" color="text.secondary" display="block" noWrap>
                  {game.artist}
                </Typography>
              )}
            </Box>
          </Box>
        )}
        <h3>{t(cardData.titleTranslationKey)}</h3>
        <div
          className="LockedFeatureContent__Message"
          dangerouslySetInnerHTML={{
            __html: t(cardData.content.leftTranslationKey),
          }}
        ></div>
        {cardData.id !== LockedFeatureId.Empty && (
          <Box my={3}>
            <Divider />
          </Box>
        )}
        <p style={{ fontStyle: 'italic' }}>
          {cardData.ctaTranslationKey ? (
            <Trans i18nKey={cardData.ctaTranslationKey} components={{ email: <Link href={lockedFeatureService.supportEmailUrl} /> }} />
          ) : (
            t('locked-feature:cta_text')
          )}
        </p>
        <ContactButton href={cardData.contactHref} />
      </Grid>
      {imageUrls.length > 0 && !hideImage && (
        <Grid item xs={12} md={7}>
          {imageUrls.length === 1 ? (
            <img className="LockedFeatureContent__Image" src={imageUrls[0]} alt={cardData.titleTranslationKey}></img>
          ) : (
            <ImageSlider
              imageUrls={imageUrls}
              count={1}
              padding={4}
              showIndex={true}
              infinite={true}
              floatingButtons={false}
              imageClassName="LockedFeatureContent__Image"
            />
          )}
        </Grid>
      )}
      {!!selectedVideo && (
        <>
          {isTutorialLoading ? (
            <GRCircularProgress />
          ) : (
            <Grid item xs={12} md={7}>
              <FtueVideoPlayer screenRatio={16 / 9} hideChapterList hideCloseButton video={selectedVideo} />
            </Grid>
          )}
        </>
      )}
    </Grid>
  )
}

export const LockedFeature = {
  Dialog: LockedFeatureDialog,
  Card: LockedFeatureCard,
  Title: LockedFeatureTitle,
}
